<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 14px;">
            <div class="header-icon-wrap"><i class="fe fe-shield" style="font-size: 22px;"></i></div>
            <div>
              <h4 class="page-title text-white">Independent providers</h4>
              <small style="opacity: .8;">Facilities that registered themselves, outside any agency</small>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">
        <div v-if="forbidden" class="alert alert-warning">Only Claimify platform administrators can review facilities.</div>

        <template v-else>
          <ul class="nav nav-pills mb-3">
            <li v-for="s in statuses" :key="s.value" class="nav-item">
              <button type="button" class="nav-link" :class="{ active: status === s.value }" @click="setStatus(s.value)">{{ s.label }}</button>
            </li>
          </ul>

          <div class="card shadow-sm table-card">
            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead class="table-head">
                  <tr>
                    <th class="table-th">Facility</th>
                    <th class="table-th">Contact</th>
                    <th class="table-th">Location</th>
                    <th class="table-th">Licence</th>
                    <th class="table-th">Status</th>
                    <th class="table-th"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in rows" :key="r.id" class="table-row">
                    <td class="px-4">
                      <strong>{{ r.facility && r.facility.name }}</strong>
                      <small class="d-block text-muted">{{ r.facility && r.facility.care_level }} · registered {{ r.facility && (r.facility.registered_at || "").slice(0, 10) }}</small>
                      <small class="d-block text-muted">{{ r.patients }} patients · fee ₦{{ Number(r.consultation_fee).toLocaleString() }}</small>
                    </td>
                    <td class="small">
                      {{ r.facility && r.facility.contact }}<br />
                      <a :href="'mailto:' + (r.facility && r.facility.email)">{{ r.facility && r.facility.email }}</a><br />
                      {{ r.facility && r.facility.phone_number }}
                    </td>
                    <td class="small">{{ r.facility && r.facility.address }}<br />{{ r.facility && r.facility.lga }}, {{ r.facility && r.facility.state }}</td>
                    <td class="small">
                      <span v-if="r.licence_number">{{ r.licence_number }}<br /></span>
                      <a v-if="r.licence_document_url" :href="r.licence_document_url" target="_blank" rel="noopener">View document</a>
                      <span v-if="!r.licence_number && !r.licence_document_url" class="text-muted">—</span>
                    </td>
                    <td>
                      <span class="ipa-status" :class="'ipa-status--' + r.status">{{ r.status }}</span>
                      <small v-if="r.status_reason" class="d-block text-muted mt-1">{{ r.status_reason }}</small>
                      <small v-if="r.decided_by" class="d-block text-muted">by {{ r.decided_by }}, {{ (r.decided_at || "").slice(0, 10) }}</small>
                    </td>
                    <td class="text-right pr-4 text-nowrap">
                      <button v-if="r.status !== 'approved'" type="button" class="btn btn-sm btn-success mb-1" @click="decide(r, 'approved')">Approve</button>
                      <button v-if="r.status === 'pending'" type="button" class="btn btn-sm btn-outline-danger mb-1 ml-1" @click="decide(r, 'rejected')">Reject</button>
                      <button v-if="r.status === 'approved'" type="button" class="btn btn-sm btn-outline-danger mb-1" @click="decide(r, 'suspended')">Suspend</button>
                      <button type="button" class="btn btn-sm btn-outline-secondary mb-1 ml-1" title="Paystack subaccount" @click="subaccount(r)">
                        <i class="fe fe-credit-card"></i>
                      </button>
                      <small v-if="r.paystack_subaccount_code" class="d-block text-muted">{{ r.paystack_subaccount_code }}</small>
                    </td>
                  </tr>
                  <tr v-if="!rows.length">
                    <td colspan="6" class="text-center text-muted py-5">{{ loading ? "Loading…" : "No facilities here." }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

export default {
  components: { Navbar },
  data() {
    return {
      statuses: [
        { value: "pending", label: "Awaiting approval" },
        { value: "approved", label: "Approved" },
        { value: "suspended", label: "Suspended" },
        { value: "rejected", label: "Rejected" },
        { value: "", label: "All" },
      ],
      status: "pending",
      rows: [],
      loading: false,
      forbidden: false,
    };
  },
  created() {
    this.load();
  },
  methods: {
    setStatus(s) {
      this.status = s;
      this.load();
    },
    load() {
      this.loading = true;
      this.axios
        .get("/api/v1/auth/independent/admin/providers", { params: { status: this.status || undefined, per_page: 100 } })
        .then((res) => { this.rows = res.data.data || []; })
        .catch((err) => {
          this.rows = [];
          this.forbidden = err.response && err.response.status === 403;
        })
        .finally(() => { this.loading = false; });
    },
    decide(row, status) {
      let reason = null;
      if (status !== "approved") {
        reason = window.prompt(`Reason the facility will see (${status}):`);
        if (!reason) return;
      } else if (!window.confirm(`Approve ${row.facility.name}? It can then enrol patients, record encounters, hold consultations and order supplies.`)) {
        return;
      }
      this.axios
        .post(`/api/v1/auth/independent/admin/providers/${row.id}/status`, { status, reason })
        .then(() => {
          this.$toasted.success(`Facility ${status}`, { position: "top-center", duration: 2500 });
          this.load();
        })
        .catch((err) => this.fail(err));
    },
    subaccount(row) {
      const code = window.prompt("Paystack subaccount code (ACCT_…). Leave empty to collect payments on the platform account.", row.paystack_subaccount_code || "");
      if (code === null) return;
      this.axios
        .put(`/api/v1/auth/independent/admin/providers/${row.id}/subaccount`, { paystack_subaccount_code: code.trim() || null })
        .then(() => this.load())
        .catch((err) => this.fail(err));
    },
    fail(err) {
      const body = (err.response && err.response.data) || {};
      this.$toasted.error(body.errors ? Object.values(body.errors).flat().join(" ") : body.message || "Failed", { position: "top-center", duration: 4000 });
    },
  },
};
</script>

<style scoped>
.ipa-status { display: inline-block; border-radius: 20px; padding: 3px 10px; font-size: .74rem; font-weight: 700; text-transform: capitalize; }
.ipa-status--pending { background: #fef3c7; color: #b45309; }
.ipa-status--approved { background: #dcfce7; color: #15803d; }
.ipa-status--suspended, .ipa-status--rejected { background: #fee2e2; color: #b91c1c; }
</style>
